import React from 'react'
import injectSheet from 'react-jss'
import Button from 'pobble-components/dist/button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch'
import { faBan } from '@fortawesome/pro-regular-svg-icons/faBan'
import { faCrown } from '@fortawesome/pro-solid-svg-icons/faCrown'
import { Link, useHistory, useRouteMatch } from 'react-router-dom'
import { useState } from 'react'
import { useEffect } from 'react'
import { apiClient } from 'pobble-components/dist/api'
import ButtonElement from 'pobble-components/dist/button-element'
import { Image } from 'pobble-components/dist/image'
import { useSubscriptionDetails } from 'pobble-components/dist/subscriptions/utils'
import { PromptsLayout } from '../../../prompts-layout'
import useFreePrompts from '../../../../common/utils'

const SearchBox = injectSheet({
  container: {
    position: 'relative',
    width: '100%',
    '& svg': {
      position: 'absolute',
      left: '16px',
      top: '16px',
      color: 'var(--light-gray)'
    }
  },
  input: {
    display: 'block',
    width: '100%',
    font: 'var(--body)',
    height: '48px',
    background: 'var(--gray)',
    borderRadius: '24px',
    padding: '0 116px 0 40px',
    border: 'none',
    color: '#fff',
    '&:focus': {
      boxShadow: '0 0 0 4px #191919, 0 0 0 5px var(--gray)'
    },
    '&::-webkit-search-cancel-button': {
      filter: 'contrast(0%)'
    }
  },
  button: {
    position: 'absolute',
    right: '4px',
    top: '4px',
    height: '40px',
    borderRadius: '20px'
  }
})(({ classes, onChange, defaultValue }) => {
  return (
    <div className={classes.container}>
      <input
        autoFocus
        defaultValue={defaultValue}
        onChange={onChange}
        name="search"
        placeholder="Search..."
        className={classes.input}
        type="search"
      />
      <FontAwesomeIcon icon={faSearch} />
      <Button type="submit" minw white className={classes.button}>
        Search
      </Button>
    </div>
  )
})

const Prompt = injectSheet({
  prompt: {
    overflow: 'hidden',
    position: 'relative',
    background: '#242424',
    borderRadius: '8px',
    paddingBottom: '56.2%',
    '& *': {
      pointerEvents: 'none'
    }
  },
  holder: {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%'
  },
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  footer: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    width: '100%',
    textAlign: 'center',
    padding: '120px 12px 12px',
    background: 'linear-gradient(to top, rgba(0, 0, 0, .8), rgba(0, 0, 0, 0))',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    font: 'var(--heading--)',
    opacity: '0',
    animation: 'appear .3s forwards',
    gap: '8px'
  },
  age: {
    display: 'block',
    background: 'rgba(255, 255, 255, .3)',
    color: '#fff',
    padding: '4px 8px',
    borderRadius: '4px',
    font: 'var(--body--)',
    fontWeight: 'bold',
    transform: 'translateY(-4px)',
    opacity: '0',
    animation: 'appear .6s ease-out forwards'
  },
  crown: {
    position: 'absolute',
    right: '8px',
    top: '8px',
    width: '24px',
    height: '24px',
    borderRadius: '24px',
    fontSize: '10px',
    background: 'rgba(0, 0, 0, .3)',
    color: 'var(--yellow)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '20'
  },
  '@keyframes appear': {
    to: {
      transform: 'translateY(0)',
      opacity: '1'
    }
  }
})(({ presentation, classes, premium, onClick }) => {
  return (
    <ButtonElement
      onClick={onClick}
      className={`${classes.prompt} ${
        premium ? 'metrics__restricted--search' : ''
      }`}>
      {premium && (
        <span className={classes.crown}>
          <FontAwesomeIcon icon={faCrown} />
        </span>
      )}
      <span className={classes.holder}>
        <Image
          className={classes.img}
          src={presentation.image_medium_url}
          alt={presentation.title}
        />
      </span>
      <span className={classes.footer}>
        <span className={classes.age}>
          Age {presentation.year_group_range.from}-{
            presentation.year_group_range.to
          }
        </span>
        <span className={classes.title}>{presentation.title}</span>
      </span>
    </ButtonElement>
  )
})

const PromptsGrid = injectSheet({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gridGap: '24px',
    padding: '32px 0 24px',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  skeleton: {
    background: '#242424',
    borderRadius: '8px',
    paddingBottom: '56.2%'
  }
})(({ searchTerm, classes, emptyState, onClick, isPremium }) => {
  const [presentations, setPresentations] = useState(null)

  useEffect(
    () => {
      if (searchTerm) {
        apiClient
          .get('v3/presentations/search', {
            params: {
              search_string: searchTerm,
              from: 0,
              limit: 30,
              categories: [
                'theme_based',
                'quick_writes',
                'lesson_starters',
                'news',
                'pobble365',
                'pobble365_plus'
              ]
            }
          })
          .then(res => setPresentations(res.data.presentations))
      }
    },
    [searchTerm]
  )

  if (presentations && !presentations.length) {
    return emptyState
  }

  return (
    <div className={classes.container}>
      <div className={classes.grid}>
        {!presentations &&
          [...Array(12)].map((_, i) => (
            <div className={classes.skeleton} key={i} />
          ))}
        {presentations &&
          presentations.map(presentation => (
            <Prompt
              onClick={() => onClick(presentation)}
              premium={isPremium(presentation)}
              presentation={presentation}
              key={presentation.id}
            />
          ))}
      </div>
    </div>
  )
})

export const Search = injectSheet({
  container: {
    background: '#191919',
    color: '#fff',
    minHeight: '100vh',
    padding: '0 24px 48px'
  },
  header: {
    position: 'sticky',
    top: '0',
    zIndex: '50',
    background: '#191919'
  },
  search: {
    padding: '24px',
    maxWidth: '700px',
    display: 'flex',
    margin: '0 auto'
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: 'var(--light-gray)',
    font: 'var(--body)',
    height: 'calc(100vh - 300px)',
    '& svg': {
      fontSize: '56px',
      marginBottom: '24px',
      opacity: '.2'
    },
    '& h2': {
      margin: '0 0 16px'
    },
    '& p': {
      color: '#ccc',
      margin: '16px 0'
    },
    '& > div': {
      maxWidth: '420px'
    },
    '& ul': {
      listStyle: 'none',
      margin: '0',
      padding: '0',
      display: 'flex',
      justifyContent: 'center',
      gap: '12px'
    },
    '& a': {
      color: 'var(--blue)'
    }
  }
})(({ classes }) => {
  const history = useHistory()
  const { params } = useRouteMatch()
  const freePromptIds = useFreePrompts()

  const { hasAccessToTL } = useSubscriptionDetails()

  return (
    <PromptsLayout>
      <div className={classes.container}>
        <header className={classes.header}>
          <form
            onSubmit={e => {
              e.preventDefault()
              history.push(`/prompt/search/${e.target.search.value}`)
            }}
            className={classes.search}>
            <SearchBox
              onChange={e => {
                if (!e.target.value) history.push(`/prompt/search`)
              }}
              key={params.searchTerm}
              defaultValue={params.searchTerm}
            />
          </form>
        </header>
        {params.searchTerm && (
          <PromptsGrid
            isPremium={presentation => {
              if (hasAccessToTL) return false
              if (!freePromptIds) return true
              return !freePromptIds.has(presentation.id)
            }}
            onClick={presentation => {
              window.scrollTo({ top: 0 })
              history.push(`/prompt/${presentation.id}`)
            }}
            searchTerm={params.searchTerm}
            emptyState={
              <div className={classes.empty}>
                <div>
                  <div>
                    <FontAwesomeIcon icon={faBan} />
                  </div>
                  <h2>Can't find what you want?</h2>
                  <p>
                    Check out <Link to={`/prompt/calendar`}>Pobble 365</Link>,{' '}
                    <Link to={`/prompt/weekly`}>Weekly writes</Link> or our{' '}
                    <Link to={`/prompt/topics`}>Topic lessons</Link>.
                  </p>
                </div>
              </div>
            }
          />
        )}
        {!params.searchTerm && (
          <div className={classes.empty}>
            <div>
              <div>
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <h2>
                What are<br />you searching for?
              </h2>
              <p>
                Search for a specific topic<br />or try one of the keywords
                below
              </p>
              <ul>
                <li>
                  <Link to={`/prompt/search/chocolate`}>Chocolate</Link>
                </li>
                <li>
                  <Link to={`/prompt/search/water`}>Water</Link>
                </li>
                <li>
                  <Link to={`/prompt/search/commas`}>Commas</Link>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </PromptsLayout>
  )
})
